import { useCallback } from "react";
import {
    getPRD5010_R01List,
    getPRD5010_R01ProductResultList,
    exportPRD5010_R01Excel,
    postPRD5010_R01KpidataInsert,
} from "./Api_PRD5010_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { ShowSuccessFetch, showError, showWarning } from "src/utils/toast/toastUtil";
import { formatDateYYYYMMDD } from "src/utils/string/stringUtil";
import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";
import { usePRD5010_R01Store, defaultPRD5010_R01Filter, PRD5010_R01Filter } from "./Store_PRD5010_R01";
import { PRD5010_R01Dto, PRD5010_R01ProductResultDto } from "./Dto_PRD5010_R01";
import { fetchDataSetHandler, fetchFilterConfig } from "src/utils/grid/GridHandlerContent";


interface PRD5010_R01ContentProps {
    setMessage: (message: string) => void;
}

export const Content_PRD5010_R01 = (props: PRD5010_R01ContentProps) => {
    const {
		setMessage,
    } = props;

    const { 
		PRD5010_R01List, setPRD5010_R01List,
		setPRD5010_R01ProductResultList,
		initSelectedPRD5010_R01ProductResultList,
		PRD5010_R01Filter, setPRD5010_R01Filter,
		PRD5010_R01PastFilter, setPRD5010_R01PastFilter,
		PRD5010_R01ProductResultList,
		selectedPRD5010_R01List,
		setAllCount,
		setScrollTop,
		setDetailScrollTop,
	} = usePRD5010_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();

	useFetchActionbarAuth("PRD5010_R01");

	const getParams = (filter: Partial<PRD5010_R01Filter>) => {
		return {
			compCd: filter.compCd,
			kpicertkey: filter.kpicertkey,
			kpifldcd: filter.kpifldcd,
			ocrdttmDateRange: filter.ocrdttmDateRange,
		};
	}



    const mapProductResultList = (rows: PRD5010_R01ProductResultDto[]) =>
        rows.map((row, idx) => ({
            ...row,
            valueIndex: idx,
            orderIndex: idx + 1,
            orderIndexSeq: 0,
        }));

    const fetchData = useCallback(async () => {
        setScrollTop(null);
        setDetailScrollTop(null);
        setLoadingSpinner(true);
        try {
            const filterParams = getParams(PRD5010_R01Filter);
            const [kpiResponse, productResultResponse] = await Promise.all([
                getPRD5010_R01List('', filterParams),
                getPRD5010_R01ProductResultList(filterParams),
            ]);

            const newList = [...(kpiResponse.data.data ?? [])] as PRD5010_R01Dto[];
            const productResultRows = mapProductResultList(
                [...(productResultResponse.data.data ?? [])] as PRD5010_R01ProductResultDto[]
            );

            setPRD5010_R01List(newList);
            setPRD5010_R01ProductResultList(productResultRows);
            initSelectedPRD5010_R01ProductResultList();

            if (newList.length > 0 || productResultRows.length > 0) {
                setPRD5010_R01PastFilter(PRD5010_R01Filter);
                ShowSuccessFetch(
                    setMessage,
                    `조회가 완료되었습니다. (KPI ${newList.length}건 / 생산실적 ${productResultRows.length}건)`
                );
            } else {
                showWarning('데이터가 없습니다.');
            }
        } catch (error) {
            console.error('데이터 조회 실패:', error);
            showWarning('데이터 조회에 실패했습니다.');
            setPRD5010_R01List([]);
            setPRD5010_R01ProductResultList([]);
            initSelectedPRD5010_R01ProductResultList();
        } finally {
            setLoadingSpinner(false);
        }
    }, [
        PRD5010_R01Filter,
        setPRD5010_R01List,
        setPRD5010_R01ProductResultList,
        initSelectedPRD5010_R01ProductResultList,
        setPRD5010_R01PastFilter,
        setMessage,
        setLoadingSpinner,
        setScrollTop,
        setDetailScrollTop,
    ]);

    // const handlePrintClick = async () => {
    //     const taxBillNo = selectedPRD5010_R01List?.[0];
    //     if (!taxBillNo) {
    //         showWarning("출력할 KPI 실적을 선택하세요.");
    //         setMessage("출력할 KPI 실적을 선택하세요.");
    //         return;
    //     }
    //     try {
    //         await printPRD5010_R01(taxBillNo, 'AGG');
    //     } catch (error) {
    //         setMessage("PDF 출력 중 오류가 발생했습니다.");
    //     }
    // };

    // // 상세 출력 모드 함수 추가
    // const handlePrintDetailClick = async () => {
    //     const taxBillNo = selectedPRD5010_R01List?.[0];
    //     if (!taxBillNo) {
    //         showWarning("출력할 KPI 실적을 선택하세요.");
    //         setMessage("출력할 KPI 실적을 선택하세요.");
    //         return;
    //     }
    //     try {
    //         await printPRD5010_R01(taxBillNo, 'EXPAND');
    //     } catch (error) {
    //         setMessage("PDF 출력 중 오류가 발생했습니다.");
    //     }
    // };
    
    const collectUniqueProductJobdates = (list: PRD5010_R01ProductResultDto[]) => {
        const jobdates = list
            .map(row => formatDateYYYYMMDD(row.productDt))
            .filter(jobdate => !!jobdate);
        return Array.from(new Set(jobdates)).sort();
    };

    const handleKpiSendClick = useCallback(async () => {
        const jobdates = collectUniqueProductJobdates(PRD5010_R01ProductResultList);

        if (jobdates.length === 0) {
            showWarning('KPI 전송할 생산일자가 없습니다. 생산실적 조회 후 실행하세요.');
            return;
        }

        setLoadingSpinner(true);
        try {
            for (const jobdate of jobdates) {
                await postPRD5010_R01KpidataInsert(jobdate);
            }

            const filterParams = getParams(PRD5010_R01Filter);
            const kpiResponse = await getPRD5010_R01List('', filterParams);
            const newList = [...(kpiResponse.data.data ?? [])] as PRD5010_R01Dto[];
            setPRD5010_R01List(newList);

            ShowSuccessFetch(
                setMessage,
                `KPI 전송이 완료되었습니다. (${jobdates.length}일: ${jobdates.join(', ')})`
            );
        } catch (error) {
            console.error('KPI 전송 실패:', error);
            showError('KPI 전송에 실패했습니다.');
        } finally {
            setLoadingSpinner(false);
        }
    }, [
        PRD5010_R01ProductResultList,
        PRD5010_R01Filter,
        setPRD5010_R01List,
        setMessage,
        setLoadingSpinner,
    ]);

    const handleExcelClick = useCallback(async () => {


        const response = await exportPRD5010_R01Excel(
            '',
            getParams(PRD5010_R01Filter)
        );

        downloadSettingExcel({ responseData: response.data.data, fileName: 'KPI 실적 조회-excel.xlsx' });
    }, [PRD5010_R01Filter, PRD5010_R01PastFilter, PRD5010_R01List.length]);
    
    return {
        fetchData,
        handleExcelClick,
        handleKpiSendClick,
        // handlePrintClick,
        // handlePrintDetailClick,
    };

};
